import React from 'react';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import UserAvatar, {
  UserAvatarProps,
} from 'shared/components/vxl/avatar/UserAvatar/UserAvatar';
import { AvatarWithLabelProps } from 'shared/components/vxl/AvatarCard/AvatarCard';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface AvatarItemProps {
  item: AvatarWithLabelProps;
  isDisabled: boolean;
}

const AvatarItem = ({ item, isDisabled }: AvatarItemProps) => {
  const pressableAnalytics = usePressableAnalytics({
    analyticsId: item.analyticsId,
    customAttributes: item.customAttributes,
  });

  const handlePress = (item: AvatarWithLabelProps) => {
    item.onPress();
    pressableAnalytics();
  };

  return (
    <PressableWithDelay
      onPress={() => handlePress(item)}
      key={item?.avatarProps?.id}
      style={({ pressed, hovered, focused }) => [
        styles.userAvatarContainer,
        hovered && styles.hovered,
        pressed && styles.pressed,
        focused && styles.focused,
        isDisabled && styles.disabledPointer,
      ]}
      testID="AvatarItem"
    >
      {item.avatarProps.name === VXLComponentId.USER_AVATAR ? (
        <UserAvatar {...(item.avatarProps.props as UserAvatarProps)} />
      ) : null}
      {item.avatarProps.name === VXLComponentId.ICON_AVATAR ? (
        <View style={styles.iconAvatar}>
          <IconAvatar {...item.avatarProps.props} variant="MD" />
        </View>
      ) : null}
      {item?.labelText ? (
        <BodyXSText
          style={[isDisabled ? styles.disabledStyle : null, styles.labelText]}
          numberOfLines={2}
          ellipsizeMode="tail"
        >
          {item?.labelText}
        </BodyXSText>
      ) : null}
    </PressableWithDelay>
  );
};

const styles = StyleSheet.create({
  disabledPointer: {
    pointerEvents: 'none',
  },
  disabledStyle: {
    color: theme.color.interactive.disabled.text,
  },
  focused: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hovered: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  iconAvatar: {
    borderRadius: theme.spacing.xs,
    height: 40,
    width: 40,
  },
  labelText: {
    marginTop: 2,
    textAlign: 'center',
  },
  pressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  userAvatarContainer: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: theme.color.interactive.secondary,
    flex: 1,
    padding: theme.spacing.s,
  },
});

export default AvatarItem;
